import React, {useState} from 'react';
import {useTranslation, withTranslation} from "react-i18next";
import PromotionHelper from "../../../../services/Menu/services/Promotion/PromotionHelper";
import Utils from "../../../../utils/Utils";
import Money from "../../../../components/Common/Money";
import AppConfig from "../../../../services/AppConfig";
import {ReactComponent as MenuItemDarkIco} from "../../../../../images/icons/menus/menu-item-dark.svg";
import {ReactComponent as MenuItemIco} from "../../../../../images/icons/menus/menu-item.svg";
import {ReactComponent as PlusIco} from "../../../../../images/icons/plus.svg";
import RoundedIcon from "../../../../components/Common/RoundedIcon";
import AvailableHelper from "../../../../services/Menu/services/Availability/AvailableHelper";
import HoursHelper from "../../../../services/Menu/services/Availability/HoursHelper";
import DateService from "../../../../services/Common/Date/DateService";
import {connect} from "react-redux";
import {RootState} from "../../../../../reducers";
import {getAvailabilities, getMenus} from "../../../../services/Menu/reducer";
import {getSession, selectLocale} from "../../../../services/Session/reducer";
import {MenuProductViewType, OrderType} from "../../../../services/Api/types";
import MenuCategoryProductAvailabilityInfo from "./MenuCategoryProductAvailabilityInfo";
import {getStoreConfig} from "../../../../services/Store/reducer";
import CategoryHelper from "../../../../services/Menu/services/Category/CategoryHelper";
import {getParsedSessionOrderType} from "../../../../utils/order";
import ItemHelper from "../../../../services/Menu/services/Item/ItemHelper";
import StoreUtils from "../../../../services/Store/StoreUtils";
import {getCart} from "../../../../services/Cart/reducer";
import AgeRestrictedContext from "../../../../services/Common/AgeRestrictedContext";

interface OwnProps {
    addItem: (id: number) => void;
    product: any;
    promotions: any[];
    forceNotAvailable?: true;
    viewType: MenuProductViewType;
    category: Record<string, any> | undefined;
    itemsExpanded?: boolean;
    hideDescription?: boolean;
    showCartQuantity?: boolean;
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

const MenuCategoryProduct = ({
                                 addItem,
                                 product,
                                 promotions,
                                 availabilities,
                                 session,
                                 locale,
                                 forceNotAvailable,
                                 viewType,
                                 config,
                                 category,
                                 menus,
                                 itemsExpanded,
                                 hideDescription,
                                 showCartQuantity,
                                 cart
                             }: Props) => {
    const {t} = useTranslation();
    let item = JSON.parse(JSON.stringify(product));
    let itemName = item.name;
    const itemGroup = product.type === "ITEM_GROUP" ? product : undefined;
    let itemDisabled = item.disabled;
    const isReadonly = StoreUtils.isOrderingReadonly(config);
    const [hasNoAvailableInfo, setHasNoAvailableInfo] = useState(false);

    if (product.type && product.type === "ITEM_GROUP") {
        item = product.item;
        itemName = product.name;
    }

    const availability = AvailableHelper.getAvailability(availabilities, item.availability_id);
    const isOpen = availability ? HoursHelper.isOpen(availability.hours, DateService.emptyOrNow(session.pickup_at)) && AvailableHelper.isAvailabile(availability, DateService.emptyOrNow(session.pickup_at)) : true;
    const orderType = session.order_type as OrderType;

    const getIsAvailableForItem = () => {
        let available = isOpen && !itemDisabled;

        if (!category || !category.originalIds || category.originalIds.length < 2) return available;

        const itemFromCategoryItems = ItemHelper.getItemFromCategoryItems(item, itemGroup, category.entities);
        const menusWithCategory = CategoryHelper.getMenusForCategoryByIds(category, menus);
        const menusWithCategoryForItem = itemFromCategoryItems ? menusWithCategory.filter((menu: Record<string, any>) => itemFromCategoryItems.menuAvailabilitiesIds.includes(menu.availability_id)) : [...menusWithCategory];
        const menusWithCategoryForCurrentOrderType = menusWithCategoryForItem.filter((menu: Record<string, any>) => menu[getParsedSessionOrderType(session.order_type as OrderType).toLowerCase()] === true);
        const menuAvailabilitiesForCurrentType = AvailableHelper.getAvailabilities(availabilities, menusWithCategoryForCurrentOrderType.map((menu: Record<string, any>) => menu.availability_id));
        const isCategoryAvailableByHoursForCurrentType = HoursHelper.isOpen(menuAvailabilitiesForCurrentType.flatMap((availability: Record<string, any>) => availability.hours), DateService.emptyOrNow(session.pickup_at));
        const isCategoryAvailableByAvailabilityDates = menuAvailabilitiesForCurrentType.some((availability: Record<string, any>) => AvailableHelper.isAvailabile(availability, DateService.emptyOrNow(session.pickup_at)));
        const isCategoryAvailable = isReadonly ? true : isCategoryAvailableByHoursForCurrentType && isCategoryAvailableByAvailabilityDates && menusWithCategoryForCurrentOrderType.length > 0;

        return available && isCategoryAvailable;
    }

    const isAvailable = getIsAvailableForItem();

    const shouldRenderAsDisabled = () => {
        if (isReadonly) {
            const isSuspended = !ItemHelper.isAvailabile(item, availabilities, DateService.emptyOrNow(undefined), undefined, {
                skipAvailability: true,
                skipStatus: true,
                skipType: true
            });
            const isEnabled = item.status === "ENABLED";

            return isSuspended || !isEnabled || hasNoAvailableInfo;
        }

        return !isAvailable;
    }

    const renderDescription = () => {
        if (item.description) return item.description;

        if (!item.selected_items || item.selected_items.length <= 0) {
            return null;
        }

        const descriptions: string[] = [];
        item.selected_items.forEach(function (selectedItem: any) {
            descriptions.push(selectedItem.quantity > 1 ? selectedItem.quantity + "x" + selectedItem.translation.name : selectedItem.translation.name);
        });

        return descriptions.join(", ");
    }

    const renderTags = () => {
        const itemPromotions = PromotionHelper.getForItem(promotions, item.id);

        if (!itemPromotions || itemPromotions.length <= 0) return null;

        if (viewType === MenuProductViewType.HORIZONTAL || viewType === MenuProductViewType.VERTICAL_BIG_IMAGE) {
            const tags = itemPromotions.map((promotion: any) => promotion.name).join(", ");

            return (
                <span className="badge badge-success-light tag">{tags}</span>
            )
        }

        return (
            <div className={"tags"}>
                {itemPromotions.map((promotion: any) => (
                    <span key={promotion.id} className="badge badge-success-light">{promotion.name}</span>
                ))}
            </div>
        )
    }

    const drawPrice = () => {
        return (
            item.discount_price ? (
                <div className="prices price-bottom">
                    <span className="price price-discount">
                        <Money value={item.price} currency='PLN'/>
                    </span>
                    <span className="price text-danger">
                        <Money value={item.discount_price} currency='PLN'/>
                    </span>
                </div>
            ) : (item.price && item.price > 0) ? (
                <span className="price price-bottom">
                    <Money value={item.price} currency='PLN'/>
                </span>
            ) : null
        );
    }

    const getItemImageLink = () => {
        switch (viewType) {
            case MenuProductViewType.VERTICAL_BIG_IMAGE:
                return item.image_medium;
            case MenuProductViewType.HORIZONTAL:
            case MenuProductViewType.VERTICAL_SMALL_IMAGE:
            default:
                return item.image_small;
        }
    }

    const itemImageLink = getItemImageLink();

    const getItemClassNameBasedOnAvailability = () => {
        if (viewType === MenuProductViewType.HORIZONTAL) {
            if (forceNotAvailable) {
                return `menu-item-not-available`;
            }
        } else {
            if (forceNotAvailable) {
                return `menu-item-not-available ${!itemsExpanded || !itemImageLink ? 'with-colors' : ''}`;
            }
        }

        return !shouldRenderAsDisabled() ? "" : "menu-item-not-available";
    }

    const getCartQuantity = () => {
        const cartItems = cart.items;

        if (itemGroup) {
            const itemGroupItemIds = itemGroup.items.map((item: Record<string, any>) => item.item_id);
            const itemGroupItemsInCart = cartItems.filter((cartItem: Record<string, any>) => itemGroupItemIds.includes(cartItem.item_id));

            return itemGroupItemsInCart.reduce((acc: number, cartItem: Record<string, any>) => acc + cartItem.quantity, 0);
        }

        return cartItems.filter((cartItem: Record<string, any>) => cartItem.item_id === item.id).reduce((acc: number, cartItem: Record<string, any>) => acc + cartItem.quantity, 0);
    }

    if (showCartQuantity) getCartQuantity();

    return (
        <div className={`menu-item ${getItemClassNameBasedOnAvailability()} ${isReadonly ? "readonly" : ""}`}
             onClick={() => addItem(item.id)}>
            <div className="card">
                <div className="card-img">
                    <div>
                        {
                            itemImageLink ? <img src={itemImageLink}
                                                 alt={t("common.word.product_image")}/> : AppConfig.isDarkMode() ?
                                <MenuItemDarkIco className="image-svg"/> : <MenuItemIco className="image-svg"/>
                        }
                        <div className="rounded-icons-container">
                            {
                                showCartQuantity && getCartQuantity() > 0 && (
                                    <div className="rounded-icon small quantity-icon">
                                        <div className="ellipse"/>
                                        {getCartQuantity()}
                                    </div>
                                )
                            }
                            {
                                !AgeRestrictedContext.isAccepted(StoreUtils.isAgeRestrictionDisabled(config)) && item.energy_drink ? (
                                    <></>
                                ) : (
                                    <RoundedIcon size="small" icon={<PlusIco/>}
                                                 forceEllipseInDarkMode={viewType === MenuProductViewType.VERTICAL_BIG_IMAGE ? true : undefined}
                                                 className={viewType === MenuProductViewType.VERTICAL_BIG_IMAGE ? "" : "text-primary"}/>
                                )
                            }
                        </div>
                    </div>
                    {viewType === MenuProductViewType.HORIZONTAL && (
                        <>
                            {renderTags()}
                            <MenuCategoryProductAvailabilityInfo availability={availability}
                                                                 isAvailable={isAvailable}
                                                                 locale={locale} forceNotAvailable={forceNotAvailable}
                                                                 item={item} orderType={orderType} menus={menus}
                                                                 pickupDate={session.pickup_at} config={config}
                                                                 category={category} availabilities={availabilities}
                                                                 session={session}
                                                                 viewType={viewType} itemGroup={itemGroup}
                                                                 setHasNoAvailableInfo={setHasNoAvailableInfo}
                            />
                        </>
                    )}
                    {viewType === MenuProductViewType.VERTICAL_BIG_IMAGE && renderTags()}
                </div>
                <div className="card-body">
                    {
                        viewType === MenuProductViewType.VERTICAL_SMALL_IMAGE && (
                            <>
                                <MenuCategoryProductAvailabilityInfo availability={availability}
                                                                     isAvailable={isAvailable}
                                                                     locale={locale}
                                                                     forceNotAvailable={forceNotAvailable}
                                                                     item={item} orderType={orderType}
                                                                     pickupDate={session.pickup_at}
                                                                     config={config}
                                                                     category={category}
                                                                     menus={menus}
                                                                     session={session}
                                                                     availabilities={availabilities}
                                                                     viewType={viewType} itemGroup={itemGroup}
                                                                     setHasNoAvailableInfo={setHasNoAvailableInfo}
                                />
                                <h1 className="card-title">{itemName}</h1>
                            </>
                        )
                    }
                    {
                        viewType === MenuProductViewType.HORIZONTAL && (
                            <>
                                {drawPrice()}
                                <h1 className="card-title">{itemName}</h1>
                            </>
                        )
                    }
                    {
                        viewType === MenuProductViewType.VERTICAL_BIG_IMAGE && (
                            <>
                                <MenuCategoryProductAvailabilityInfo availability={availability}
                                                                     isAvailable={isAvailable}
                                                                     locale={locale}
                                                                     forceNotAvailable={forceNotAvailable}
                                                                     item={item} orderType={orderType}
                                                                     pickupDate={session.pickup_at}
                                                                     config={config}
                                                                     category={category}
                                                                     menus={menus}
                                                                     session={session}
                                                                     availabilities={availabilities}
                                                                     viewType={viewType} itemGroup={itemGroup}
                                                                     setHasNoAvailableInfo={setHasNoAvailableInfo}
                                />
                                {drawPrice()}
                                <h1 className="card-title">{itemName}</h1>
                            </>
                        )
                    }
                    {hideDescription !== true && (
                        <p className="card-text"
                           dangerouslySetInnerHTML={{__html: Utils.nl2brString(renderDescription())}}/>
                    )}
                    {
                        viewType === MenuProductViewType.VERTICAL_SMALL_IMAGE && (
                            <>
                                {renderTags()}
                                {drawPrice()}
                            </>
                        )
                    }
                </div>
            </div>
        </div>
    )
}

const mapStateToProps = (state: RootState) => ({
    availabilities: getAvailabilities(state.menu),
    session: getSession(state.session),
    locale: selectLocale(state.session),
    config: getStoreConfig(state.config),
    menus: getMenus(state.menu),
    cart: getCart(state.cart)
})

const connector = connect(mapStateToProps);
export default withTranslation()(connector(MenuCategoryProduct));
